import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";

/**
 * Hooks around /api/v1/contact-messages/ — read + triage for messages
 * submitted via the public /contact/ form. Endpoint is locked to
 * Director+ (Director / CEO / Admin); regular users will see a 403.
 *
 * NOTE: creation is intentionally not exposed via the API — visitors
 * submit through the public form (a server-rendered Django view), which
 * writes the row directly. Only listing, mark-as-read and delete are
 * available here.
 */

export interface ContactMessageRow {
  id: number;
  name: string;
  email: string;
  subject: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

const KEY = ["contact-messages"];

// ---------- READ ----------

export function useContactMessages(opts: { unread?: boolean | null; search?: string } = {}) {
  return useQuery<ContactMessageRow[]>({
    queryKey: [...KEY, "list", opts],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts.unread === true)  params.set("unread", "true");
      if (opts.unread === false) params.set("unread", "false");
      if (opts.search)           params.set("search", opts.search);
      const { data } = await api.get<ContactMessageRow[] | { results: ContactMessageRow[] }>(
        `/contact-messages/${params.toString() ? "?" + params.toString() : ""}`,
      );
      return Array.isArray(data) ? data : (data?.results ?? []);
    },
    staleTime: 30_000,
  });
}

/** Cheap counter for sidebar / topbar badges. */
export function useUnreadContactMessageCount(opts: { enabled?: boolean } = {}) {
  return useQuery<{ unread: number }>({
    enabled: opts.enabled ?? true,
    queryKey: [...KEY, "unread-count"],
    queryFn: async () => {
      const { data } = await api.get<{ unread: number }>("/contact-messages/unread_count/");
      return data;
    },
    staleTime: 30_000,
  });
}

// ---------- WRITE ----------

/** PATCH `is_read` on a single message. Used by both "mark read" and
 *  "mark unread" — sender passes the desired boolean. */
export function useUpdateContactMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_read }: { id: number; is_read: boolean }) => {
      const { data } = await api.patch<ContactMessageRow>(
        `/contact-messages/${id}/`,
        { is_read },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteContactMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/contact-messages/${id}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** One-shot: flip every unread row to read. Returns {updated: N}. */
export function useMarkAllContactMessagesRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ updated: number }>("/contact-messages/mark_all_read/");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// ---------- BULK WRITE ----------

export function useBulkUpdateContactMessages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: number[]; is_read: boolean }) => {
      const results = await Promise.allSettled(
        input.ids.map((id) =>
          api.patch(`/contact-messages/${id}/`, { is_read: input.is_read })),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      return { ok, fail };
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useBulkDeleteContactMessages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => api.delete(`/contact-messages/${id}/`)),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      return { ok, fail };
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
